import { ReactElement, useEffect, useRef, useState } from 'react';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import invariant from 'tiny-invariant';
import type { TaskDragData } from './useTaskDraggable';
import type { TaskStatus } from '../../types';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box';
import { Td, Tr } from '@ff-it/ui';

type TaskDropZoneProps = {
  status: TaskStatus;
};

export function TaskDropZone({ status }: TaskDropZoneProps): ReactElement {
  const elementRef = useRef<HTMLTableRowElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);

  useEffect(() => {
    invariant(elementRef.current);
    const element = elementRef.current;

    return dropTargetForElements({
      element,
      canDrop({ source }) {
        const sourceData = source.data as TaskDragData;
        return sourceData.currentStatus !== status;
      },
      getData({ input }) {
        return attachClosestEdge({ taskId: -1, currentStatus: status, index: 0 } as TaskDragData, {
          element,
          input: { ...input, clientX: 0, clientY: 0 },
          allowedEdges: ['top'],
        });
      },
      onDragEnter() {
        setIsDraggedOver(true);
      },
      onDragLeave() {
        setIsDraggedOver(false);
      },
      onDrop() {
        setIsDraggedOver(false);
      },
    });
  }, [status]);

  return (
    <Tr style={{ position: 'relative' }} ref={elementRef}>
      <Td>{isDraggedOver && <DropIndicator edge="top" />}</Td>
      <Td colSpan={6} color="disabled" fontSize="sm">
        Drop task to change the status
      </Td>
    </Tr>
  );
}
